import { Alert, AlertIcon, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { useCallback } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { SwitchInput } from "src/components/Inputs/SwitchInput";
import { updateFormRuleActiveStatus } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { RawFormEngineRule } from "src/types/formEngineRule";
import * as GQL from "src/types/graphql";
import { FormTemplateDefaultCard } from "../components/FormTemplateDefaultCard";
import * as RuleUtils from "./utils";

const findStaleTagIDs = (
  enrollmentPeriodTagIDs: string[],
  ruleTags: string[]
): string[] => ruleTags.filter((tag) => !enrollmentPeriodTagIDs.includes(tag));

const RULE_TYPE_DISPLAY_NAMES: Record<GQL.form_engine_rule_type_enum, string> =
  {
    [GQL.form_engine_rule_type_enum.AUTO_PLACEMENT]: "Auto placement",
    [GQL.form_engine_rule_type_enum.AUTO_WAITLIST]: "Auto waitlist",
    [GQL.form_engine_rule_type_enum.DYNAMIC_SIBLING_TAG]: "Auto tag",
    [GQL.form_engine_rule_type_enum.QUESTION_TAG]: "Auto tag",
    [GQL.form_engine_rule_type_enum.GEOGRAPHIC_PREFERENCE_TAG]: "Auto tag",
  };

const getRuleTypeDisplayName = (
  rule: RawFormEngineRule,
  ruleInfo: RuleUtils.RuleInfo
): string => {
  if (
    rule.rule_type === GQL.form_engine_rule_type_enum.AUTO_WAITLIST &&
    ruleInfo.enable_automatic_offers
  ) {
    return "Auto waitlist and offer";
  }
  return RULE_TYPE_DISPLAY_NAMES[
    rule.rule_type as GQL.form_engine_rule_type_enum
  ];
};

export type Props = {
  formTemplateId: string;
  index: number;
  rule: RawFormEngineRule;
  ruleInfo: RuleUtils.RuleInfo;
  tags: GQL.GetLegacyFormTemplateRuleByFormTemplateId_enrollment_period_tag[];
  tagIdToNameMap: Map<string, string>;
  isDisabled?: boolean;
  onClick: () => Promise<void>;
  onEditClick?: () => void;
  onRefetch: () => Promise<void>;
};
export const FormEngineRuleCard: React.FC<Props> = ({
  formTemplateId,
  index,
  rule,
  ruleInfo,
  tags,
  tagIdToNameMap,
  isDisabled = false,
  onEditClick,
  onClick,
  onRefetch,
}) => {
  const toast = useAvelaToast();
  const { confirm: confirmActiveChange, confirmationDialog: activateDialog } =
    useConfirmationDialog({
      header: "Activate rule?",
      body: (
        <Text>
          This will enable the automatic behavior for this rule immediately.
        </Text>
      ),
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Activate rule",
      },
      translate: true,
    });

  const {
    confirm: confirmInactiveChange,
    confirmationDialog: inactivateDialog,
  } = useConfirmationDialog({
    header: "Deactivate rule?",
    body: (
      <Text>
        This will stop the automatic behavior for this rule immediately.
      </Text>
    ),
    cancelButton: {
      label: "Cancel",
    },
    confirmButton: {
      label: "Deactivate rule",
    },
    translate: true,
  });

  const handleSubmit = useCallback(async () => {
    try {
      await updateFormRuleActiveStatus({
        path: { formTemplateId, id: rule.id },
        body: { active: !rule.active },
      });
      toast({
        title: "Success",
        description: `Rule ${rule.active ? "deactivated" : "activated"}`,
        status: "success",
        duration: 2000,
      });
      await onRefetch();
    } catch (e) {
      toast({
        title: "Something went wrong",
        description: "Failed to change rule status",
        status: "error",
        duration: 2000,
      });
    }
  }, [formTemplateId, rule, toast, onRefetch]);

  const handleActiveChangeClick = useCallback(async () => {
    if (rule.active) {
      if (!(await confirmInactiveChange())) {
        return;
      }
    } else {
      if (!(await confirmActiveChange())) {
        return;
      }
    }

    handleSubmit();
  }, [confirmActiveChange, handleSubmit, confirmInactiveChange, rule.active]);

  return (
    <>
      <FormTemplateDefaultCard
        alert={
          findStaleTagIDs(
            tags.map((t) => t.id),
            ruleInfo.tag_ids
          ).length > 0 && (
            <Alert status="warning" borderRadius="0.375rem">
              <AlertIcon />
              <Text>
                This{" "}
                <Text as="span" fontWeight="700">
                  rule is paused
                </Text>{" "}
                because it is missing at least one required field.
              </Text>
            </Alert>
          )
        }
        key={`${rule.rule_type}-${index}`}
        tag={
          <Flex gap={2} flexWrap="wrap">
            <Text
              fontSize="sm"
              fontWeight="bold"
              color="gray.700"
              bg="white"
              border="1px solid"
              borderColor="gray.300"
              borderRadius="md"
              px={2}
              py={1}
              display="inline-block"
              width="fit-content"
            >
              {getRuleTypeDisplayName(rule, ruleInfo)}
            </Text>
            {ruleInfo.tag_ids.map((tagId) => {
              const tagName = tagIdToNameMap.get(tagId);
              if (!tagName) return null;
              return (
                <Text
                  key={tagId}
                  fontSize="sm"
                  fontWeight="bold"
                  color="gray.700"
                  bg="gray.100"
                  borderRadius="md"
                  px={2}
                  py={1}
                  display="inline-block"
                  width="fit-content"
                >
                  {tagName}
                </Text>
              );
            })}
          </Flex>
        }
        title={ruleInfo.name}
        description={ruleInfo.description}
        switches={
          <SwitchInput<boolean>
            checked={[true, "Active"]}
            unchecked={[false, "Inactive"]}
            value={rule.active}
            onChange={handleActiveChangeClick}
            switchDirection="row"
          />
        }
        actionSwitch={
          <Button
            leftIcon={<Icon as={RiDeleteBin6Line} />}
            variant="outline"
            colorScheme="gray"
            size="sm"
            isDisabled={isDisabled}
            onClick={onClick}
          >
            Delete
          </Button>
        }
        onEditClick={onEditClick}
      />
      {activateDialog}
      {inactivateDialog}
    </>
  );
};
